'use client';
// PriceChart Component - Redesigned with Recharts to match reference
import { useMemo, useState } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { GraphSkeleton } from '@/components/skeletons';
import { useT } from '@/hooks';
import type { ChartDataRow, DataPoint, MarketConfig, PriceChartProps } from '@/types';
import { HoverSyncTooltip } from './HoverSyncTooltip';
import { PriceChartHeader } from './PriceChartHeader';
import { PriceChartHoverLabel } from './PriceChartHoverLabel';

function formatXAxisDate(timestamp: number, range: string): string {
  const date = new Date(timestamp * 1000);

  switch (range) {
    case '1H':
    case '6H':
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    case '1D':
      return date.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true });
    case '1W':
    case '1M':
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    case 'ALL':
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
    default:
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
}

function renderYAxisReferenceLines(ticks: number[]) {
  return ticks.map((tick) => (
    <ReferenceLine key={tick} y={tick} stroke="rgba(128, 128, 128, 0.1)" strokeDasharray="4 4" />
  ));
}

const CustomActiveDot = (props) => {
  const { cx, cy, stroke } = props;

  return (
    <g>
      {/* Large faint halo */}
      <circle
        cx={cx}
        cy={cy}
        r={12}
        fill={stroke}
        fillOpacity={0.15}
        style={{ pointerEvents: 'none' }}
      />
      {/* Solid center dot */}
      <circle
        cx={cx}
        cy={cy}
        r={6}
        fill={'#fff'}
        stroke={stroke}
        strokeWidth={2}
        style={{ pointerEvents: 'none' }}
      />
    </g>
  );
};

function renderMarketLines(lineMarkets: Array<MarketConfig & { value?: number }>) {
  return lineMarkets.map((market) => (
    <Line
      key={market.marketId}
      type="monotone"
      dataKey={`market_${market.marketId}`}
      stroke={market.color}
      strokeWidth={2}
      dot={false}
      connectNulls={true}
      activeDot={<CustomActiveDot stroke={market.color} />}
      isAnimationActive={false}
    />
  ));
}

export function PriceChart({ markets, data, selectedRange, isLoading }: PriceChartProps) {
  const { t } = useT();
  const [hoverPayload, setHoverPayload] = useState<Record<string, unknown> | null>(null);
  const [hoverX, setHoverX] = useState<number | null>(null);

  const chartData = useMemo(() => {
    if (!data.length) return [];

    // Use latest data point as reference for time range filtering (pure - no Date.now)
    const latestTimestamp = Math.max(...data.map((d) => d.timestamp));
    const referenceTime = latestTimestamp;

    let cutoffTimestamp = 0;
    const HOUR = 3600;

    switch (selectedRange) {
      case '1H':
        cutoffTimestamp = referenceTime - HOUR;
        break;
      case '6H':
        cutoffTimestamp = referenceTime - 6 * HOUR;
        break;
      case '1D':
        cutoffTimestamp = referenceTime - 24 * HOUR;
        break;
      case '1W':
        cutoffTimestamp = referenceTime - 7 * 24 * HOUR;
        break;
      case '1M':
        cutoffTimestamp = referenceTime - 30 * 24 * HOUR;
        break;
      case 'ALL':
      default:
        cutoffTimestamp = 0;
    }

    return data.filter((point) => point.timestamp >= cutoffTimestamp);
  }, [data, selectedRange]);

  const uniqueChartData = useMemo(() => {
    const map = new Map<number, DataPoint>();

    for (const point of chartData) {
      // if same timestamp appears, overwrite (keeps latest)
      map.set(point.timestamp, point);
    }

    // sort ascending
    return Array.from(map.values()).sort((a, b) => a.timestamp - b.timestamp);
  }, [chartData]);

  // Transform data for Recharts - one line per market
  const transformedData = useMemo<ChartDataRow[]>(() => {
    if (!uniqueChartData.length || !markets.length) return [];

    return uniqueChartData.map((point) => {
      const basePoint: ChartDataRow = {
        date: point.date || new Date(point.timestamp * 1000).toISOString(),
        timestamp: point.timestamp,
        formattedDate: point.timestamp,
      };

      const marketValues: Record<string, number | undefined> = {};
      markets.forEach((market, index) => {
        // Read flat price{index} from data point (legacy format)
        // Check for both nested point.values (new) and flat keys (old)
        const nestedValue = point.values ? point.values[market.marketId] : undefined;
        const flatValue = point[`price${index}`];

        marketValues[`market_${market.marketId}`] =
          nestedValue !== undefined ? nestedValue : flatValue;
      });

      return {
        ...basePoint,
        ...marketValues,
      };
    });
  }, [uniqueChartData, markets]);

  // Calculate Y-axis domain - more zoomed (tighter range around data)
  const yAxisDomain = useMemo(() => {
    if (transformedData.length === 0) return [0, 0.4];

    // Get all market values
    const allValues: number[] = [];
    markets.forEach((market) => {
      transformedData.forEach((d) => {
        const value = d[`market_${market.marketId}`] as number;
        if (value !== undefined) allValues.push(value);
      });
    });

    if (allValues.length === 0) return [0, 0.4];

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);

    // Balanced zoom: add 5% padding for cleaner look with multiple lines
    const padding = (max - min) * 0.05 || 0.02;
    const domainMin = Math.max(0, min - padding);
    const domainMax = Math.min(1, max + padding);

    return [domainMin, domainMax];
  }, [transformedData, markets]);

  // Generate Y-axis ticks based on zoomed domain
  const [yMin, yMax] = yAxisDomain;
  const yRange = yMax - yMin;
  let yStep;
  if (yRange <= 0.05) yStep = 0.01;
  else if (yRange <= 0.1) yStep = 0.02;
  else if (yRange <= 0.2) yStep = 0.05;
  else if (yRange <= 0.4) yStep = 0.1;
  else yStep = 0.2;

  const yAxisTicks: number[] = [];
  const startTick = Math.ceil(yMin / yStep) * yStep;
  for (let value = startTick; value <= yMax; value += yStep) {
    yAxisTicks.push(Math.round(value * 100) / 100);
  }

  // Active row for header + legend (hovered row or latest)
  const activeRow = useMemo<ChartDataRow | null>(() => {
    if (hoverPayload) return hoverPayload as ChartDataRow;
    if (transformedData.length === 0) return null;
    return transformedData[transformedData.length - 1] as ChartDataRow;
  }, [hoverPayload, transformedData]);

  // Top 3 markets by value at the active row
  const topThreeMarkets = useMemo<Array<MarketConfig & { value: number }>>(() => {
    if (!activeRow) return [];

    return markets
      .map((market) => {
        const key = `market_${market.marketId}`;
        const value = (activeRow[key] as number | undefined) ?? 0;
        return { ...market, value };
      })
      .sort((a, b) => b.value - a.value)
      .slice(0, 3);
  }, [activeRow, markets]);

  // Timestamp for header (from active row)
  const headerTimestamp =
    activeRow &&
    ((activeRow.timestamp as number | undefined) ?? (activeRow.date as string | undefined));

  function handleHover(
    row: Record<string, unknown> | null,
    coordinate: { x?: number; y?: number } | null
  ) {
    setHoverPayload(row);
    setHoverX(coordinate?.x ?? null);
  }

  function xAxisTickFormatter(value: number) {
    return formatXAxisDate(value, selectedRange);
  }

  function yAxisTickFormatter(value: number) {
    return `${(value * 100).toFixed(0)}%`;
  }

  if (isLoading) {
    return <GraphSkeleton />;
  }

  if (transformedData.length === 0 || markets.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          {t('market.noDataAvailableForTimeRange', {
            defaultValue: 'No data available for the selected time range.',
          })}
        </Typography>
      </Box>
    );
  }

  // Only show top 3 markets on the chart as well
  const lineMarkets = topThreeMarkets.length ? topThreeMarkets : markets.slice(0, 3);
  const referenceLines = renderYAxisReferenceLines(yAxisTicks);
  const marketLines = renderMarketLines(lineMarkets);

  return (
    <Box sx={{ width: '100%', position: 'relative' }} data-chart-container>
      {/* Header with top 3 markets - positioned above chart */}
      <PriceChartHeader markets={topThreeMarkets} />

      <Box sx={{ position: 'relative', height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={transformedData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
            <CartesianGrid
              strokeDasharray="4 4"
              stroke="rgba(128, 128, 128, 0.2)"
              vertical={false}
            />
            <XAxis
              dataKey="timestamp"
              type="number"
              domain={['dataMin', 'dataMax']}
              tick={{ fontSize: 11, fill: 'rgba(128, 128, 128, 0.6)' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={xAxisTickFormatter}
              minTickGap={30}
              tickCount={6}
            />
            <YAxis
              domain={yAxisDomain}
              ticks={yAxisTicks}
              tick={{ fontSize: 11, fill: 'rgba(128, 128, 128, 0.6)' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={yAxisTickFormatter}
              width={45}
              orientation="right"
            />
            {referenceLines}

            {/* Single line per top market - no highlight on hover */}
            {marketLines}

            <Tooltip
              content={<HoverSyncTooltip onHover={handleHover} />}
              cursor={{
                stroke: 'rgba(128, 128, 128, 0.4)',
                strokeWidth: 1,
                strokeDasharray: '2 2',
              }}
              wrapperStyle={{ display: 'none' }}
            />
          </LineChart>
        </ResponsiveContainer>

        {/* Hover date/time label aligned with cursor (top of dotted line) */}
        <PriceChartHoverLabel x={hoverX} timestamp={headerTimestamp} />
      </Box>

      {/* Bottom controls */}
      {/* <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          mt: 2,
        }}
      >
        <ChartControls />
      </Box> */}
    </Box>
  );
}

export default PriceChart;
